import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PrintIcon from '@mui/icons-material/Print';

import { PermissionGuard } from '../../../../auth/components/PermissionGuard';
import { GuiaProduccionService } from '../../guias-produccion/services/GuiaProduccionService';
import { PrecioProveedorService } from '../../precios-proveedor/services/precioProveedorService';
import { OrdenLiquidacionService } from '../services/ordenLiquidacionService';

import type {
  GuiaProduccion,
  GuiaProduccionDetailResponse,
  GuiaProduccionDetalleResponse,
} from '../../guias-produccion/types';
import type { PrecioProveedor } from '../../precios-proveedor/types';
import type { OrdenLiquidacion, EstadoType } from '../types';
import { estadoLabels } from '../types';
import { TIPOS_OPERACION, tipoOperacionLabels } from '../../precios-proveedor/types';
import { ordenLiquidacionPdfService } from '../services/ordenLiquidacionPdfService';

// --- Constants ---

const TASA_DETRACCION = 0.015;

// --- Helper types ---

interface DetalleAgrupado {
  presentacion: string;
  calibre: string;
  kilos: number;          // Kilos para Materia Prima (excluye devoluciones internas y externas)
  kilosServicio: number;  // Kilos para Servicio (incluye devolución interna, excluye externa)
  precioMP: number;
  montoMP: number;
  precioServicio: number;
  montoServicio: number;
  precioEncontrado: boolean;
  tieneDevolucionInterna: boolean;
  // For saving new prices
  idPresentacion?: number;
  idCalibre?: number;
}

// --- Component ---

const OrdenLiquidacionFormPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const guiaId = Number(searchParams.get('guiaId') || 0);

  // Data state
  const [guiaDetail, setGuiaDetail] = useState<GuiaProduccionDetailResponse | null>(null);
  const [guiaListItem, setGuiaListItem] = useState<GuiaProduccion | null>(null);
  const [precios, setPrecios] = useState<PrecioProveedor[]>([]);

  // Form state - pre-populate from URL params (when editing existing order)
  const [tipoProceso, setTipoProceso] = useState<string>(searchParams.get('tipoProceso') || '');
  const [tipoOperacion, setTipoOperacion] = useState<string>(searchParams.get('tipoOperacion') || '');

  // Inline price edits: key = "presentacion|calibre", value = { precioMP, precioServicio }
  const [precioEdits, setPrecioEdits] = useState<Record<string, { precioMP: number; precioServicio: number }>>({});

  // Existing order state
  const [ordenExistente, setOrdenExistente] = useState<OrdenLiquidacion | null>(null);
  const [updatingEstado, setUpdatingEstado] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Loading / errors
  const [loadingGuia, setLoadingGuia] = useState(false);
  const [loadingPrecios, setLoadingPrecios] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preciosLoaded, setPreciosLoaded] = useState(false);

  // Read-only when order exists and is not pending
  const isReadOnly = ordenExistente != null && ordenExistente.estado !== 'PEN';

  // Refs for preventing reset on initial render
  const initialTipoProceso = React.useRef(searchParams.get('tipoProceso') || '');
  const isFirstRender = React.useRef(true);

  // --- Load guia data and existing order info ---

  useEffect(() => {
    if (!guiaId) return;

    const loadGuia = async () => {
      setLoadingGuia(true);
      setError(null);
      try {
        const [detail, listResult] = await Promise.all([
          GuiaProduccionService.getById(guiaId),
          GuiaProduccionService.getAll(1, 1000),
        ]);
        setGuiaDetail(detail);

        const found = listResult.data.find((g) => g.id === guiaId) || null;
        setGuiaListItem(found);

        // Try to load existing order for this guía
        try {
          const ordenes = await OrdenLiquidacionService.getAll();
          const orden = ordenes.find((o) => o.idGuiaProduccion === guiaId);
          if (orden) {
            setOrdenExistente(orden);
            if (!searchParams.get('tipoProceso')) {
              setTipoProceso(orden.tipoProceso);
              setTipoOperacion(orden.tipoOperacion);
              initialTipoProceso.current = orden.tipoProceso;
            }
          }
        } catch {
          // No existing order, that's fine
        }
      } catch {
        setError('Error al cargar los datos de la guia de produccion.');
      } finally {
        setLoadingGuia(false);
      }
    };

    loadGuia();
  }, [guiaId, searchParams]);

  // --- Load prices when tipoProceso + tipoOperacion selected ---

  const idProveedor = guiaListItem?.idProveedor ?? guiaDetail?.idProveedor;

  const loadPrecios = useCallback(async () => {
    if (!idProveedor || !tipoProceso || !tipoOperacion) return;

    setLoadingPrecios(true);
    setPreciosLoaded(false);
    setPrecioEdits({});
    try {
      const result = await PrecioProveedorService.getByProveedorAndTipo(
        idProveedor,
        tipoProceso,
        tipoOperacion
      );
      setPrecios(result);

      // Pre-populate edits with existing prices so they're editable
      const edits: Record<string, { precioMP: number; precioServicio: number }> = {};
      for (const p of result) {
        const pres = p.presentacion?.descripcion || '';
        const cal = p.calibre?.descripcion || '';
        if (pres && cal) {
          edits[`${pres}|${cal}`] = {
            precioMP: p.precioMateriaPrima,
            precioServicio: p.precioServicio,
          };
        }
      }
      setPrecioEdits(edits);

      setPreciosLoaded(true);
    } catch {
      setPrecios([]);
      setPreciosLoaded(true);
    } finally {
      setLoadingPrecios(false);
    }
  }, [idProveedor, tipoProceso, tipoOperacion]);

  useEffect(() => {
    if (tipoProceso && tipoOperacion && idProveedor) {
      loadPrecios();
    }
  }, [tipoProceso, tipoOperacion, idProveedor, loadPrecios]);

  // Reset tipoOperacion when tipoProceso changes (skip if pre-populated)
  useEffect(() => {
    if (isFirstRender.current && tipoProceso === initialTipoProceso.current) {
      isFirstRender.current = false;
      return;
    }
    isFirstRender.current = false;
    setTipoOperacion('');
    setPrecios([]);
    setPreciosLoaded(false);
    setPrecioEdits({});
  }, [tipoProceso]);

  // --- Match prices to detalles ---

  const findPrecio = useCallback(
    (presentacion: string, calibre: string): PrecioProveedor | undefined => {
      return precios.find(
        (p) =>
          p.presentacion?.descripcion?.toLowerCase() === presentacion.toLowerCase() &&
          p.calibre?.descripcion?.toLowerCase() === calibre.toLowerCase()
      );
    },
    [precios]
  );

  // --- Group detalles ---

  const detallesAgrupados = useMemo((): DetalleAgrupado[] => {
    if (!guiaDetail?.detalles) return [];

    // Group by presentacion + calibre
    const groups = new Map<string, {
      kilosMP: number;
      kilosServicio: number;
      tieneDevInterna: boolean;
      idPresentacion?: number;
      idCalibre?: number;
      detalles: GuiaProduccionDetalleResponse[];
    }>();

    for (const det of guiaDetail.detalles) {
      // Skip solo devoluciones externas (lo que se llevó el proveedor)
      // Las devoluciones internas SÍ se procesan: descuentan MP pero suman al servicio
      if (det.esDevueltoExterno) continue;

      const pres = det.presentacion || 'Sin Presentacion';
      const cal = det.calibre || 'Sin Calibre';
      const key = `${pres}|${cal}`;

      const coef = det.coeficiente && det.coeficiente > 0 ? det.coeficiente : 1;
      const cantidadDevInt = det.esDevueltoInterno ? (det.cantidadDevolucionInterna || 0) : 0;

      // Kilos para MP: solo lo que NO se devolvió internamente
      const kilosMP = Math.max(0, det.cantidad - cantidadDevInt) * coef;
      // Kilos para Servicio: TODO lo procesado (incluye devolución interna)
      const kilosServicio = det.cantidad * coef;

      const existing = groups.get(key);
      if (existing) {
        existing.kilosMP += kilosMP;
        existing.kilosServicio += kilosServicio;
        existing.tieneDevInterna = existing.tieneDevInterna || cantidadDevInt > 0;
        existing.detalles.push(det);
      } else {
        groups.set(key, {
          kilosMP,
          kilosServicio,
          tieneDevInterna: cantidadDevInt > 0,
          idPresentacion: det.idPresentacion,
          idCalibre: det.idCalibre,
          detalles: [det]
        });
      }
    }

    const result: DetalleAgrupado[] = [];

    for (const [key, group] of groups) {
      const [pres, cal] = key.split('|');
      const precio = findPrecio(pres, cal);
      const edit = precioEdits[key];

      const precioMP = edit?.precioMP ?? precio?.precioMateriaPrima ?? 0;
      const precioServ = edit?.precioServicio ?? precio?.precioServicio ?? 0;

      result.push({
        presentacion: pres,
        calibre: cal,
        kilos: group.kilosMP,
        kilosServicio: group.kilosServicio,
        precioMP,
        montoMP: group.kilosMP * precioMP,
        precioServicio: precioServ,
        montoServicio: group.kilosServicio * precioServ,
        precioEncontrado: !!precio,
        tieneDevolucionInterna: group.tieneDevInterna,
        idPresentacion: precio?.idPresentacion || group.idPresentacion,
        idCalibre: precio?.idCalibre || group.idCalibre,
      });
    }

    return result;
  }, [guiaDetail, findPrecio, precioEdits]);

  // --- Calculations ---

  const servicioAplica = !(tipoProceso === 'EXTERNO' && tipoOperacion === 'PROD_B_KILOS');

  const subTotal = useMemo(
    () => detallesAgrupados.reduce((sum, d) => sum + d.montoMP, 0),
    [detallesAgrupados]
  );

  const detraccion = subTotal * TASA_DETRACCION;

  const totalServicio = useMemo(
    () => (servicioAplica ? detallesAgrupados.reduce((sum, d) => sum + d.montoServicio, 0) : 0),
    [detallesAgrupados, servicioAplica]
  );

  const totalLiquidar = subTotal - detraccion - totalServicio;

  // --- Check if there are missing prices or zero prices ---

  const todosLosPreciosCompletos = useMemo(
    () => preciosLoaded && detallesAgrupados.length > 0 && detallesAgrupados.every((d) => d.precioMP > 0),
    [detallesAgrupados, preciosLoaded]
  );

  const totalPositivo = totalLiquidar > 0;

  // --- Inline price edit handlers ---

  const handlePrecioEdit = (key: string, field: 'precioMP' | 'precioServicio', value: string) => {
    const numValue = parseFloat(value) || 0;
    setPrecioEdits((prev) => ({
      ...prev,
      [key]: {
        precioMP: prev[key]?.precioMP ?? 0,
        precioServicio: prev[key]?.precioServicio ?? 0,
        [field]: numValue,
      },
    }));
  };


  // --- Generate orden (auto-saves prices first) ---

  const handleGenerarOrden = async () => {
    if (!guiaId || !idProveedor || !tipoProceso || !tipoOperacion) return;

    if (!todosLosPreciosCompletos) {
      setError('Debe ingresar todos los precios de materia prima antes de generar la orden.');
      return;
    }

    if (!totalPositivo) {
      setError('El total a liquidar debe ser positivo.');
      return;
    }

    setGenerando(true);
    setError(null);
    try {
      // Auto-save prices to PrecioProveedor
      const preciosToSave: PrecioProveedor[] = [];
      for (const det of detallesAgrupados) {
        const key = `${det.presentacion}|${det.calibre}`;
        const edit = precioEdits[key];
        if (!edit || (edit.precioMP === 0 && edit.precioServicio === 0)) continue;

        const existingPrecio = findPrecio(det.presentacion, det.calibre);
        preciosToSave.push({
          id: existingPrecio?.id || 0,
          idProveedor,
          idPresentacion: existingPrecio?.idPresentacion || det.idPresentacion || 0,
          idCalibre: existingPrecio?.idCalibre || det.idCalibre || 0,
          tipoProceso,
          tipoOperacion,
          precioMateriaPrima: edit.precioMP,
          precioServicio: edit.precioServicio,
          fechaActualizacion: new Date().toISOString(),
          activo: true,
        });
      }

      if (preciosToSave.length > 0) {
        await PrecioProveedorService.saveBulk({
          idProveedor,
          tipoProceso,
          tipoOperacion,
          precios: preciosToSave,
        });
      }

      // Generate the order
      await OrdenLiquidacionService.generar(guiaId);
      navigate('/administracion/orden-liquidacion');
    } catch {
      setError('Error al generar la orden de liquidacion.');
    } finally {
      setGenerando(false);
    }
  };

  // --- Update estado (Select dropdown style, same as ParteProduccion) ---

  const handleEstadoChange = async (event: SelectChangeEvent) => {
    const nuevoEstado = event.target.value as EstadoType;
    if (!ordenExistente) return;
    setUpdatingEstado(true);
    setError(null);
    try {
      await OrdenLiquidacionService.updateEstado(ordenExistente.id, nuevoEstado);
      setOrdenExistente({ ...ordenExistente, estado: nuevoEstado });
      setSuccessMsg(`Estado actualizado a ${estadoLabels[nuevoEstado]}`);

      // Redirigir al listado después de cambiar el estado
      setTimeout(() => {
        navigate('/administracion/orden-liquidacion');
      }, 1500);
    } catch {
      setError(`Error al cambiar el estado de la orden.`);
      setUpdatingEstado(false);
    }
  };

  // --- Print PDF ---

  const handlePrint = () => {
    if (!ordenExistente || !guiaDetail) return;

    try {
      ordenLiquidacionPdfService.generatePdf({
        numero: ordenExistente.numero,
        fecha: ordenExistente.fechaProceso,
        numeroDocumento: guiaDetail.numeroDocumento || '',
        proveedor: guiaDetail.proveedor || guiaListItem?.proveedor || '',
        asociacion: guiaDetail.asociacion || guiaListItem?.asociacion || '',
        duenio: guiaDetail.duenio || guiaListItem?.duenio || '',
        encargado: guiaDetail.encargado || guiaListItem?.encargado || '',
        mallas: ordenExistente.numeroMallas,
        subLoteCantidad: guiaDetail.subLoteCantidad || 0,
        tipoProceso,
        tipoOperacion,
        tipoOperacionLabel: tipoOperacionLabels[tipoOperacion] || tipoOperacion,
        detalles: detallesAgrupados.map((d) => ({
          presentacion: d.presentacion,
          calibre: d.calibre,
          kilos: d.kilos,
          precioMP: d.precioMP,
          montoMP: d.montoMP,
          precioServicio: d.precioServicio,
          montoServicio: d.montoServicio,
        })),
        totalKilos: detallesAgrupados.reduce((s, d) => s + d.kilos, 0),
        subTotal,
        detraccion,
        totalServicio,
        totalLiquidar,
        servicioAplica,
        observaciones: ordenExistente.observaciones || guiaDetail.observaciones || guiaDetail.notas,
      });
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Error al generar el PDF.');
    }
  };

  // --- Render helpers ---

  const formatNumber = (n: number, decimals = 2) =>
    n.toLocaleString('es-PE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  const formatCurrency = (n: number) => `S/ ${formatNumber(n)}`;

  // --- Loading state ---

  if (loadingGuia) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!guiaId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">No se especifico el ID de la guia de produccion.</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/produccion/guias-produccion')}
          sx={{ mt: 2 }}
        >
          Volver a Guias de Produccion
        </Button>
      </Box>
    );
  }

  // Group detalles by presentacion for display
  const presentaciones = [...new Set(detallesAgrupados.map((d) => d.presentacion))];

  return (
    <Box sx={{ p: 2, width: '100%', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <Paper sx={{ p: 3 }}>
        {/* Header - same style as ParteProduccionFormPage */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/administracion/orden-liquidacion')}
            sx={{ mr: 2 }}
          >
            Volver
          </Button>
          <Typography variant="h5" component="h1" fontWeight={600} sx={{ flex: 1 }}>
            {ordenExistente ? `Orden de Liquidación N° ${ordenExistente.numero}` : 'Nueva Orden de Liquidación'}
          </Typography>
          {ordenExistente && preciosLoaded && detallesAgrupados.length > 0 && (
            <Button
              variant="outlined"
              color="info"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{ mr: 2 }}
            >
              Imprimir PDF
            </Button>
          )}
        </Box>

        {/* Estado section - same style as ParteProduccionFormPage */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip label="LIQUIDACIÓN" color="info" size="medium" variant="filled" sx={{ fontWeight: 600 }} />
          {ordenExistente && (
            <PermissionGuard permission="LIQUIDACION_APROBAR" fallback={
              <Chip label={estadoLabels[ordenExistente.estado as EstadoType] || ordenExistente.estado} size="small" />
            }>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Estado:</Typography>
              <Select
                value={ordenExistente.estado}
                onChange={handleEstadoChange}
                size="small"
                sx={{ minWidth: 150 }}
                disabled={updatingEstado}
              >
                <MenuItem value="PEN">
                  <Chip label="Pendiente" color="warning" size="small" />
                </MenuItem>
                <MenuItem value="APR">
                  <Chip label="Aprobado" color="success" size="small" />
                </MenuItem>
                <MenuItem value="ANU">
                  <Chip label="Anulado" color="error" size="small" />
                </MenuItem>
              </Select>
              {updatingEstado && <CircularProgress size={20} />}
            </Box>
            </PermissionGuard>
          )}
        </Box>

        {successMsg && (
          <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 3 }}>
            {successMsg}
          </Alert>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Guia Info - FormSectionPanel style */}
        {guiaDetail && (
        <Paper variant="outlined" sx={{ mb: 2, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <Box sx={{ backgroundColor: 'grey.100', px: 3, py: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Información de la Guía de Producción
            </Typography>
          </Box>
          <Box sx={{ px: 3, py: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                N. Documento
              </Typography>
              <Typography variant="body2">{guiaDetail.numeroDocumento || '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Fecha Extraccion
              </Typography>
              <Typography variant="body2">{guiaDetail.fechaExtraccion || '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Proveedor
              </Typography>
              <Typography variant="body2">
                {guiaDetail.proveedor || guiaListItem?.proveedor || '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Asociacion
              </Typography>
              <Typography variant="body2">
                {guiaDetail.asociacion || guiaListItem?.asociacion || '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Duenio
              </Typography>
              <Typography variant="body2">
                {guiaDetail.duenio || guiaListItem?.duenio || '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Encargado
              </Typography>
              <Typography variant="body2">
                {guiaDetail.encargado || guiaListItem?.encargado || '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Sub Lote Cantidad
              </Typography>
              <Typography variant="body2">{guiaDetail.subLoteCantidad ?? '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Estado
              </Typography>
              <Typography variant="body2">
                <Chip label={guiaDetail.estado || 'N/A'} size="small" />
              </Typography>
            </Box>
          </Box>
          </Box>
        </Paper>
        )}

        {/* Tipo Proceso y Tipo Operacion - FormSectionPanel style */}
        <Paper variant="outlined" sx={{ mb: 2, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <Box sx={{ backgroundColor: 'grey.100', px: 3, py: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Configuración de Liquidación
            </Typography>
          </Box>
          <Box sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }} size="small" disabled={isReadOnly}>
            <InputLabel>Tipo Proceso</InputLabel>
            <Select
              value={tipoProceso}
              label="Tipo Proceso"
              onChange={(e) => setTipoProceso(e.target.value)}
            >
              <MenuItem value="">
                <em>Seleccionar</em>
              </MenuItem>
              <MenuItem value="INTERNO">Interno</MenuItem>
              <MenuItem value="EXTERNO">Externo</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 250 }} size="small" disabled={!tipoProceso || isReadOnly}>
            <InputLabel>Tipo Operacion</InputLabel>
            <Select
              value={tipoOperacion}
              label="Tipo Operacion"
              onChange={(e) => setTipoOperacion(e.target.value)}
            >
              <MenuItem value="">
                <em>Seleccionar</em>
              </MenuItem>
              {tipoProceso &&
                TIPOS_OPERACION[tipoProceso]?.map((op) => (
                  <MenuItem key={op} value={op}>
                    {tipoOperacionLabels[op] || op}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {loadingPrecios && <CircularProgress size={24} sx={{ alignSelf: 'center' }} />}
        </Box>

          {!idProveedor && guiaListItem && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No se pudo determinar el proveedor de esta guia. Es posible que no se pueda cargar los
              precios.
            </Alert>
          )}
          </Box>
        </Paper>

        {/* Missing prices alert */}
      {preciosLoaded && !todosLosPreciosCompletos && detallesAgrupados.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Ingrese todos los precios de materia prima para poder generar la orden. Los precios se
          guardaran automaticamente en la lista de precios del proveedor.
        </Alert>
      )}

      {/* Detalles Table */}
      {preciosLoaded && detallesAgrupados.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>Presentacion</TableCell>
                  <TableCell>Calibre</TableCell>
                  <TableCell align="right">Kilos MP</TableCell>
                  <TableCell align="right">Precio MP</TableCell>
                  <TableCell align="right">Monto MP</TableCell>
                  {servicioAplica && (
                    <>
                      <TableCell align="right">Kilos Serv.</TableCell>
                      <TableCell align="right">Precio Servicio</TableCell>
                      <TableCell align="right">Monto Servicio</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {presentaciones.map((pres) => {
                  const items = detallesAgrupados.filter((d) => d.presentacion === pres);
                  return items.map((det, idx) => {
                    const key = `${det.presentacion}|${det.calibre}`;
                    return (
                      <TableRow
                        key={key}
                        sx={{
                          bgcolor: !det.precioEncontrado
                            ? 'warning.50'
                            : det.tieneDevolucionInterna
                              ? '#fff8e1'
                              : undefined,
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <TableCell>
                          {idx === 0 ? (
                            <Typography variant="body2" fontWeight={600}>
                              {det.presentacion}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {det.calibre}
                            {det.tieneDevolucionInterna && (
                              <Chip
                                label="Dev. Int."
                                size="small"
                                color="warning"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{formatNumber(det.kilos)}</TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            variant="outlined"
                            value={precioEdits[key]?.precioMP ?? ''}
                            onChange={(e) => handlePrecioEdit(key, 'precioMP', e.target.value)}
                            sx={{ width: 110 }}
                            inputProps={{ min: 0, step: 0.01 }}
                            disabled={isReadOnly}
                          />
                        </TableCell>
                        <TableCell align="right">{formatCurrency(det.montoMP)}</TableCell>
                        {servicioAplica && (
                          <>
                            <TableCell align="right">{formatNumber(det.kilosServicio)}</TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                variant="outlined"
                                value={precioEdits[key]?.precioServicio ?? ''}
                                onChange={(e) =>
                                  handlePrecioEdit(key, 'precioServicio', e.target.value)
                                }
                                sx={{ width: 110 }}
                                inputProps={{ min: 0, step: 0.01 }}
                                disabled={isReadOnly}
                              />
                            </TableCell>
                            <TableCell align="right">{formatCurrency(det.montoServicio)}</TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  });
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell
                    colSpan={servicioAplica ? 8 : 5}
                    sx={{ borderBottom: 'none', pt: 2 }}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Totals */}
      {preciosLoaded && detallesAgrupados.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Resumen de Liquidacion
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1,
              maxWidth: 400,
              ml: 'auto',
            }}
          >
            <Typography variant="body2">SubTotal:</Typography>
            <Typography variant="body2" align="right" fontWeight={500}>
              {formatCurrency(subTotal)}
            </Typography>

            <Typography variant="body2">Detraccion (1.5%):</Typography>
            <Typography variant="body2" align="right" color="error.main">
              - {formatCurrency(detraccion)}
            </Typography>

            {servicioAplica && (
              <>
                <Typography variant="body2">Servicio:</Typography>
                <Typography variant="body2" align="right" color="error.main">
                  - {formatCurrency(totalServicio)}
                </Typography>
              </>
            )}

            <Divider sx={{ gridColumn: '1 / -1' }} />

            <Typography variant="subtitle1" fontWeight={700}>
              Total a Liquidar:
            </Typography>
            <Typography variant="subtitle1" align="right" fontWeight={700} color="primary.main">
              {formatCurrency(totalLiquidar)}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Observaciones (heredadas de la Guía de Producción) */}
      {(ordenExistente?.observaciones || guiaDetail?.observaciones || guiaDetail?.notas) && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#fff8e1' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Observaciones
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {ordenExistente?.observaciones || guiaDetail?.observaciones || guiaDetail?.notas}
          </Typography>
        </Paper>
      )}

        {/* Action Buttons - same style as ParteProduccionFormPage */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/administracion/orden-liquidacion')}
          >
            Cancelar
          </Button>

          {!isReadOnly && (
            <Button
              variant="contained"
              color="primary"
              startIcon={generando ? <CircularProgress size={20} color="inherit" /> : <ReceiptLongIcon />}
              onClick={handleGenerarOrden}
              disabled={
                generando ||
                !tipoProceso ||
                !tipoOperacion ||
                !preciosLoaded ||
                detallesAgrupados.length === 0 ||
                !todosLosPreciosCompletos ||
                !totalPositivo
              }
            >
              {generando ? 'Generando...' : 'Generar Orden'}
            </Button>
          )}
        </Box>

      </Paper>
    </Box>
  );
};

export default OrdenLiquidacionFormPage;
